/** Per-user collapsed state for the labelled section rail. Only the desktop frame can collapse
 *  it, so every other shell reads `collapsed: false` whatever is stored. Touch screens
 *  also stay expanded: the collapsed rail's labels live in hover tooltips,
 *  which a finger cannot reach, so there the toggle is hidden rather than offered. */
import { useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { DESKTOP_FRAME_TOUCH_QUERY, useDesktopFrame } from './desktopFrame';

export const SECTION_RAIL_COLLAPSED_KEY = 'quantis:section-rail-collapsed:v1';

function readStored(): boolean {
  try {
    return window.localStorage.getItem(SECTION_RAIL_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function useSectionRailCollapsed(shortcutEnabled = false) {
  const [stored, setStored] = useState(readStored);
  const desktopFrame = useDesktopFrame();
  const isTouchScreen = useMediaQuery(DESKTOP_FRAME_TOUCH_QUERY);
  const inFrame = desktopFrame && !isTouchScreen;

  const toggle = useCallback(() => {
    setStored((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SECTION_RAIL_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // Private mode: the rail still collapses for this session.
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!shortcutEnabled) return;
    const publish = () => window.dispatchEvent(new CustomEvent('section-rail-collapse:state', {
      detail: {available: inFrame, collapsed: stored && inFrame},
    }));
    publish();
    window.addEventListener('section-rail-collapse:request', publish);
    if (inFrame) window.addEventListener('section-rail-collapse:toggle', toggle);
    return () => {
      window.removeEventListener('section-rail-collapse:request', publish);
      window.removeEventListener('section-rail-collapse:toggle', toggle);
      window.dispatchEvent(new CustomEvent('section-rail-collapse:state', {detail: {available: false, collapsed: false}}));
    };
  }, [shortcutEnabled, inFrame, stored, toggle]);

  // Same Cmd/Ctrl+B the Sidebar primitive already claims; it has nothing to toggle on these rails.
  useEffect(() => {
    if (!shortcutEnabled || !inFrame) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'b' || !(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('input, textarea, select, [contenteditable="true"]')) return;
      toggle();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shortcutEnabled, inFrame, toggle]);

  return { collapsed: stored && inFrame, canToggle: inFrame, toggle };
}

/** Header mirrors the rail owner rather than maintaining another stored preference. */
export function useSectionRailCollapseHeaderState() {
  const [state, setState] = useState({available: false, collapsed: false});
  useEffect(() => {
    const receive = (event: Event) => setState((event as CustomEvent<{available: boolean; collapsed: boolean}>).detail);
    window.addEventListener('section-rail-collapse:state', receive);
    window.dispatchEvent(new CustomEvent('section-rail-collapse:request'));
    return () => window.removeEventListener('section-rail-collapse:state', receive);
  }, []);
  return state;
}
